import { ref } from 'vue';
import { threadsAPI, type Thread } from '../services/api';
import echo from '../services/echo';

const conversations = ref<Thread[]>([]);
const isLoading = ref(false);
const currentPage = ref(1);
const hasMore = ref(true);
const isArchive = ref(false);
const perPage = 10;

let organizationChannel: string | null = null;

export function useThreads() {
  const loadThreads = async (page: number = 1) => {
    if (isLoading.value) return;

    isLoading.value = true;
    try {
      const response = await threadsAPI.getThreads({
        page,
        per_page: perPage,
        archive: isArchive.value,
      });

      if (page === 1) {
        conversations.value = response.threads;
      } else {
        conversations.value = [...conversations.value, ...response.threads];
      }

      hasMore.value = response.has_more;
      currentPage.value = page;
    } catch (error: any) {
      console.error('Error loading threads:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const handleLoadMore = () => {
    if (hasMore.value && !isLoading.value) {
      loadThreads(currentPage.value + 1);
    }
  };

  const toggleArchive = () => {
    isArchive.value = !isArchive.value;
    conversations.value = [];
    currentPage.value = 1;
    hasMore.value = false;
    loadThreads(1);
  };

  const setupOrganizationChannel = (organizationId: string) => {
    if (organizationChannel) {
      echo.leave(organizationChannel);
    }

    const channelName = `organization-${organizationId}`;
    organizationChannel = channelName;

    echo
      .private(channelName)
      .listen('.thread.created', (data: any) => {
        console.log('New thread created:', data);

        const newThread: Thread = {
          id: data.id,
          name: data.client.name,
          status: 'new',
          lastMessage: undefined,
          messages: [],
          organizationId: data.organization_id,
          userId: data.user_id,
          clientId: data.client_id,
          takenUntil: data.taken_until,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          user: data.user,
          client: data.client,
        };

        conversations.value.unshift(newThread);
      })
      .error((error: any) => {
        console.error('Echo organization channel error:', error);
      });
  };

  const teardownOrganizationChannel = () => {
    if (organizationChannel) {
      echo.leave(organizationChannel);
      organizationChannel = null;
    }
  };

  return {
    conversations,
    isLoading,
    currentPage,
    hasMore,
    isArchive,
    loadThreads,
    handleLoadMore,
    toggleArchive,
    setupOrganizationChannel,
    teardownOrganizationChannel,
  };
}
